import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, ShieldCheck, Mail, Phone, Power, Target, KeyRound, Send } from "lucide-react";
import { ROLE_LABELS } from "../lib/re";
import { useAuth } from "../context/AuthContext";
import { PhotoUpload } from "../components/PhotoUpload";

const EMPTY = { name: "", email: "", password: "", send_invite: true, role: "consultor", phone: "", title: "Consultor Imobiliário", whatsapp: "" };
const PROTECTED = ["admin", "suporte"];

export default function Consultores() {
  const { user: me, refreshUser } = useAuth();
  const isAdmin = me.role === "admin";
  const canManage = (u) => isAdmin || !PROTECTED.includes(u.role);
  const roleOptions = Object.entries(ROLE_LABELS).filter(([k]) => isAdmin || !PROTECTED.includes(k));
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [goalEdits, setGoalEdits] = useState({});

  const saveGoal = async (u) => {
    const val = parseFloat(goalEdits[u.id]);
    if (isNaN(val)) return;
    await api.patch(`/users/${u.id}`, { monthly_goal: val });
    toast.success("Meta atualizada"); load();
  };

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/users", form.send_invite ? { ...form, password: null } : form);
      if (!form.send_invite) toast.success("Acesso criado");
      else if (r.data.invite_sent) toast.success(`Acesso criado e convite enviado para ${r.data.email}`);
      else toast.warning(`Acesso criado, mas o convite não foi enviado: ${r.data.invite_error}. Use "Reenviar convite" no cartão.`);
      setModal(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggle = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "Acesso desativado" : "Acesso ativado"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); toast.success("Perfil atualizado"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const invite = async (u) => {
    try { await api.post(`/users/${u.id}/invite`); toast.success(`Convite enviado para ${u.email}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`Nova palavra-passe para ${u.name} (mínimo 8 caracteres):`);
    if (!pw) return;
    try { await api.patch(`/users/${u.id}`, { password: pw }); toast.success("Palavra-passe redefinida. No próximo acesso terá de escolher uma nova."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const filtered = users.filter((u) => !q || u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase()));

  return (
    <Layout title="Utilizadores & Acessos">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="text-sm text-slate-400">{users.length} utilizadores · gestão de acessos do Grupo Almar</p>
        </div>
        <button onClick={() => setModal(true)} data-testid="new-user-btn"
          className="flex items-center gap-2 bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold px-4 py-2.5 rounded-lg transition-all hover:scale-[1.02]">
          <Plus size={18} /> Criar Novo Acesso
        </button>
      </div>

      <input className="input-almar mb-5 max-w-md" placeholder="Pesquisar consultor" value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search-input" />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="users-grid">
        {filtered.map((u) => (
          <div key={u.id} className="card-almar p-5" data-testid={`user-card-${u.id}`}>
            <div className="flex items-center gap-3">
              <div className="relative w-12 h-12 shrink-0">
                <img src={u.photo_url || "https://ui-avatars.com/api/?name=" + encodeURIComponent(u.name)} alt="" className="w-12 h-12 rounded-full object-cover ring-1 ring-[#C9A84C]/40" data-testid={`user-photo-${u.id}`} />
                {(canManage(u) || u.id === me.id) && <PhotoUpload small userId={u.id} testId={`user-photo-upload-${u.id}`} onDone={() => { load(); if (u.id === me.id) refreshUser(); }} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-100 truncate">{u.name}</div>
                <div className="text-xs text-slate-500 truncate">{u.title}</div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 ${u.role === "admin" ? "bg-[#C9A84C]/20 text-[#E0C574] border-[#C9A84C]/40" : "bg-slate-800 text-slate-300 border-slate-700"}`}>
                <ShieldCheck size={10} /> {ROLE_LABELS[u.role] || u.role}
              </span>
            </div>
            <div className="mt-3 space-y-1.5 text-xs text-slate-400">
              <div className="flex items-center gap-2 truncate"><Mail size={13} /> {u.email}</div>
              <div className="flex items-center gap-2"><Phone size={13} /> {u.phone || "—"}</div>
            </div>
            <div className="mt-4 pt-3 border-t border-[#1E3A56] flex items-center justify-between gap-2">
              <span className={`text-xs ${u.active ? "text-emerald-400" : "text-red-400"}`}>{u.active ? "● Ativo" : "● Inativo"}</span>
              {canManage(u) && u.id !== me.id ? (<>
              <select className="input-almar py-1 text-xs w-auto" value={u.role} data-testid={`role-select-${u.id}`} onChange={(e) => changeRole(u, e.target.value)}>
                {roleOptions.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              {u.active && <button onClick={() => invite(u)} data-testid={`invite-user-${u.id}`} title={u.invited_at ? "Reenviar convite por email" : "Enviar convite por email"} className="text-xs flex items-center gap-1 text-slate-400 hover:text-[#C9A84C] transition-all"><Send size={13} /> {u.invited_at ? "Reenviar" : "Convidar"}</button>}
              <button onClick={() => resetPw(u)} data-testid={`reset-pw-${u.id}`} title="Redefinir palavra-passe" className="text-xs flex items-center gap-1 text-slate-400 hover:text-[#C9A84C] transition-all"><KeyRound size={13} /></button>
              <button onClick={() => toggle(u)} data-testid={`toggle-user-${u.id}`}
                className="text-xs flex items-center gap-1 text-slate-400 hover:text-[#C9A84C] transition-all"><Power size={13} /> {u.active ? "Desativar" : "Ativar"}</button>
              </>) : <span className="text-[11px] text-slate-500" data-testid={`user-locked-${u.id}`}>{u.id === me.id ? "A sua conta" : "Gerido pelo Administrador"}</span>}
            </div>
            {u.invited_at && (
              <p className="text-[11px] mt-2 text-slate-500" data-testid={`invite-status-${u.id}`}>
                {u.activated_at ? "Conta ativada pelo convite" : `Convite enviado a ${new Date(u.invited_at).toLocaleDateString("pt-PT")} · a aguardar ativação`}
              </p>
            )}
            {u.role === "consultor" && canManage(u) && (
              <div className="mt-3 pt-3 border-t border-[#1E3A56]">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1"><Target size={11} /> Meta mensal (€)</label>
                <div className="flex gap-2">
                  <input type="number" className="input-almar py-1.5 text-sm" data-testid={`goal-input-${u.id}`}
                    defaultValue={u.monthly_goal || ""} placeholder="0"
                    onChange={(e) => setGoalEdits((g) => ({ ...g, [u.id]: e.target.value }))} />
                  <button onClick={() => saveGoal(u)} data-testid={`goal-save-${u.id}`}
                    className="text-xs bg-[#C9A84C]/15 border border-[#C9A84C]/40 text-[#E0C574] hover:bg-[#C9A84C]/25 px-3 rounded-lg transition-all whitespace-nowrap">Guardar</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setModal(false)}>
          <div className="card-almar w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl text-[#C9A84C]">Criar Novo Acesso</h3>
              <button onClick={() => setModal(false)}><X size={20} className="text-slate-400" /></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <input className="input-almar" placeholder="Nome completo" required data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input className="input-almar" type="email" placeholder="Email" required data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={form.send_invite} data-testid="user-send-invite-checkbox" onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} className="accent-[#C9A84C]" />
                Enviar convite por email para definir a palavra-passe
              </label>
              {!form.send_invite && <input className="input-almar" type="password" placeholder="Palavra-passe temporária" required minLength={8} data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />}
              <div className="grid grid-cols-2 gap-3">
                <input className="input-almar" placeholder="Telefone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value, whatsapp: e.target.value })} />
                <select className="input-almar" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select">
                  {roleOptions.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <input className="input-almar" placeholder="Cargo / título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <p className="text-[11px] text-slate-500">{form.send_invite ? "O utilizador recebe um email de boas-vindas com um link pessoal (válido 72 horas) para definir a palavra-passe." : "A palavra-passe indicada é temporária: no primeiro acesso o utilizador é obrigado a escolher uma nova."} A fotografia pode ser carregada depois no cartão do utilizador ou no perfil dele.</p>
              <button type="submit" data-testid="user-save-btn" className="w-full bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold py-2.5 rounded-lg transition-all">Criar Acesso</button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
